/**
 * Load configuration with multi-site Netlify support
 *
 * CRITICAL: This module MUST NOT depend on Pinia or Vue, as it's loaded
 * before Vue/Pinia initialization in main.ts
 *
 * Priority:
 * 1. Environment variables (VITE_* from build/Netlify) - site-specific
 * 2. /public/config.json (fallback for local development)
 * 3. Default values
 */

export interface RuntimeConfig {
  VITE_API_BASE_URL: string;
  VITE_SOCKET_SERVER_URL: string;
  VITE_SOCKET_TOPIC: string;
  VITE_SOCKET_USERNAME: string;
  VITE_SOCKET_PASSWORD: string;
  VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: string;
  VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE: string;
  /** URL base do app-cnc-monitor (deep-links dos QR codes). Vazio = QR oculto. */
  VITE_APP_BASE_URL: string;
}

let loadedConfig: RuntimeConfig | null = null;

/**
 * Load configuration with proper priority for multi-site Netlify deployments
 * Call this BEFORE initializing Vue/Pinia
 *
 * Priority: 1. Environment vars (Netlify) 2. config.json (fallback) 3. Defaults
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (loadedConfig) {
    return loadedConfig;
  }

  const envVars = import.meta.env as Record<string, string | undefined>;
  const pickValue = (
    key: keyof RuntimeConfig,
    fileValue?: string,
    fallback = "",
  ) => {
    // PRIORITY 1: Environment variables (Netlify/build-time)
    // Each Netlify site can have its own env vars
    const envValue = envVars[key];
    if (typeof envValue === "string" && envValue.trim() !== "") {
      return envValue;
    }

    // PRIORITY 2: config.json (fallback for local development)
    if (typeof fileValue === "string" && fileValue.trim() !== "") {
      return fileValue;
    }

    // PRIORITY 3: Default value
    return fallback;
  };

  try {
    // Try to load from /public/config.json as fallback
    const response = await fetch("/config.json");
    if (response.ok) {
      const fileConfig = (await response.json()) as Partial<RuntimeConfig>;
      loadedConfig = {
        VITE_API_BASE_URL: pickValue(
          "VITE_API_BASE_URL",
          fileConfig.VITE_API_BASE_URL,
        ),
        VITE_SOCKET_SERVER_URL: pickValue(
          "VITE_SOCKET_SERVER_URL",
          fileConfig.VITE_SOCKET_SERVER_URL,
        ),
        VITE_SOCKET_TOPIC: pickValue(
          "VITE_SOCKET_TOPIC",
          fileConfig.VITE_SOCKET_TOPIC,
        ),
        VITE_SOCKET_USERNAME: pickValue(
          "VITE_SOCKET_USERNAME",
          fileConfig.VITE_SOCKET_USERNAME,
        ),
        VITE_SOCKET_PASSWORD: pickValue(
          "VITE_SOCKET_PASSWORD",
          fileConfig.VITE_SOCKET_PASSWORD,
        ),
        VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: pickValue(
          "VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE",
          fileConfig.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
          "0",
        ),
        VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE: pickValue(
          "VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE",
          fileConfig.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE,
          "0",
        ),
        VITE_APP_BASE_URL: pickValue(
          "VITE_APP_BASE_URL",
          fileConfig.VITE_APP_BASE_URL,
        ),
      };
      return loadedConfig;
    }
  } catch (error) {
    console.warn(
      "[config-loader] ⚠️ Failed to load /public/config.json, using Vite environment variables",
    );
  }

  // Fallback to Vite environment variables
  loadedConfig = {
    VITE_API_BASE_URL: pickValue("VITE_API_BASE_URL"),
    VITE_SOCKET_SERVER_URL: pickValue("VITE_SOCKET_SERVER_URL"),
    VITE_SOCKET_TOPIC: pickValue("VITE_SOCKET_TOPIC"),
    VITE_SOCKET_USERNAME: pickValue("VITE_SOCKET_USERNAME"),
    VITE_SOCKET_PASSWORD: pickValue("VITE_SOCKET_PASSWORD"),
    VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: pickValue(
      "VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE",
      undefined,
      "0",
    ),
    VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE: pickValue(
      "VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE",
      undefined,
      "0",
    ),
    VITE_APP_BASE_URL: pickValue("VITE_APP_BASE_URL"),
  };

  return loadedConfig;
}

/**
 * Get loaded config (only call after loadRuntimeConfig() has completed)
 * Safe to use in Vue components and stores
 */
export function getConfig(): RuntimeConfig {
  if (!loadedConfig) {
    // Check if config was loaded globally
    const globalConfig = (
      typeof window !== "undefined" ? (window as any).__RUNTIME_CONFIG__ : null
    ) as RuntimeConfig | undefined;
    if (globalConfig) {
      loadedConfig = globalConfig;
      return loadedConfig;
    }

    console.warn(
      "[config-loader] Config not loaded yet, using environment variables directly",
    );
    // Return environment variables directly instead of cached values
    return {
      VITE_API_BASE_URL: (import.meta.env.VITE_API_BASE_URL as string) || "",
      VITE_SOCKET_SERVER_URL:
        (import.meta.env.VITE_SOCKET_SERVER_URL as string) || "",
      VITE_SOCKET_TOPIC: (import.meta.env.VITE_SOCKET_TOPIC as string) || "",
      VITE_SOCKET_USERNAME:
        (import.meta.env.VITE_SOCKET_USERNAME as string) || "",
      VITE_SOCKET_PASSWORD:
        (import.meta.env.VITE_SOCKET_PASSWORD as string) || "",
      VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE:
        (import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE as string) ||
        "0",
      VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE:
        (import.meta.env.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE as string) ||
        "0",
      VITE_APP_BASE_URL: (import.meta.env.VITE_APP_BASE_URL as string) || "",
    };
  }
  return loadedConfig;
}

export function getConfigValue<K extends keyof RuntimeConfig>(key: K): string {
  const config = getConfig();
  return config[key] || "";
}

/**
 * URL base do app-cnc-monitor, usada para montar os deep-links dos QR codes
 * (`{base}/machine/{serial}` e `{base}/fleet/{code}`).
 *
 * Retorna `null` quando não configurada — e nesse caso NENHUMA UI de QR deve
 * ser renderizada (decisão de produto: sem a env var, a feature fica oculta).
 */
export function getAppBaseUrl(): string | null {
  const raw = getConfigValue("VITE_APP_BASE_URL").trim();
  if (!raw) return null;
  return raw.replace(/\/+$/, "");
}
